import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import Alert from "@mui/material/Alert";
import AlertTitle from "@mui/material/AlertTitle";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import Typography from "@mui/material/Typography";
import { useIdentity } from "../../auth/useIdentity";
import { fetchReport, type ReportRow } from "../../api/reports";
import type { ApiError } from "../../api/errors";
import { DataTable } from "../../components/DataTable";
import { FilterBar, type FilterValues } from "../../components/FilterBar";
import type { FilterField } from "./catalog";
import { loadBilling, rowNamespaces, withPlanPrices } from "./planPrices";

export function ReportPage({
  title,
  endpoint,
  fields = [],
  required = [],
  note,
  rowsKey,
  planPrices = false,
}: {
  title: string;
  endpoint: string;
  fields?: FilterField[];
  required?: FilterField[];
  note?: string;
  /** The answer is an object with its rows under this key (Collections). */
  rowsKey?: string;
  /** All plans: a "Price incl. GST" column from billing. */
  planPrices?: boolean;
}) {
  const identity = useIdentity();
  const [filter, setFilter] = useState<FilterValues | null>(null);

  // 🔑 A report the service rejects without a date range must not be sent
  // until it has one. Running it on mount produced "start and end are
  // required" before the operator had been given anything to fill in, which
  // reads as a broken screen rather than an unfinished form. Reports with no
  // required fields still run immediately -- there is nothing to wait for.
  const ready = required.every((f) => Boolean(filter?.[f]));
  const effective = filter ?? {};

  const q = useQuery({
    queryKey: ["report", endpoint, identity.namespace, effective],
    enabled: Boolean(identity.token && identity.namespace) && ready,
    queryFn: ({ signal }) =>
      fetchReport(
        { token: identity.token, namespace: identity.namespace },
        endpoint,
        effective,
        signal,
      ),
  });

  const err = q.error as ApiError | undefined;
  // 🔑 A Go service encodes an empty result as JSON null, not [] (a nil
  // slice). Reading .length on it threw and blanked the whole console
  // (All plans, owner 2026-10-10). No rows is "No rows", never a crash.
  const body: unknown = q.data?.data;
  const list: unknown = rowsKey && body && typeof body === "object" ? (body as Record<string, unknown>)[rowsKey] : body;
  const rows = Array.isArray(list) ? (list as ReportRow[]) : q.data ? [] : undefined;
  // Collections also answers its total; shown so the sum need not be done by eye.
  const namespaces = planPrices && rows ? rowNamespaces(rows) : [];
  const billing = useQuery({
    queryKey: ["report-plan-prices", namespaces],
    enabled: Boolean(identity.token) && namespaces.length > 0,
    retry: false,
    queryFn: ({ signal }) => loadBilling(identity, namespaces, signal),
  });
  const shown = planPrices && rows ? withPlanPrices(rows, billing.data) : rows;
  const totals =
    rowsKey && body && typeof body === "object" && typeof (body as { total?: unknown }).total === "number"
      ? (body as { total: number; count?: number; currency?: string })
      : null;

  return (
    <Box>
      <Typography variant="h5" gutterBottom>
        {title}
      </Typography>

      {/* An empty table is a real answer, and an uninterpretable one without
          saying what the report means. "Nobody is online" and "the report is
          broken" look identical otherwise. */}
      {note && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {note}
        </Typography>
      )}

      <FilterBar fields={fields} required={required} onApply={setFilter} busy={q.isFetching} />

      {/* ⚠️ Contract B: a 200 can still be a NARROWED answer. A narrowed report
          is otherwise indistinguishable from one that had no such rows, so it
          is surfaced rather than silently rendered. */}
      {q.data?.narrowed && (
        <Alert severity="info" sx={{ mb: 2 }}>
          <AlertTitle>Scope narrowed</AlertTitle>
          You asked wider than your entitlement. This answer covers{" "}
          <code>{q.data.scope}</code> only.
        </Alert>
      )}

      {err && (
        <Alert severity={err.kind === "forbidden" ? "warning" : "error"} sx={{ mb: 2 }}>
          <AlertTitle>
            {err.kind === "unauthenticated" && "Not signed in"}
            {err.kind === "forbidden" && "Refused"}
            {err.kind === "fail-closed" && "Request not sent"}
            {err.kind === "not-found" && "Not available"}
            {err.kind === "at-capacity" && "Backend busy"}
            {err.kind === "bad-request" && "Bad request"}
            {err.kind === "unavailable" && "Unavailable"}
          </AlertTitle>
          {err.message}
          {err.kind === "forbidden" && (
            <Box sx={{ mt: 1, fontSize: 13 }}>
              A 403 here is either a missing role or a namespace outside your subtree. These are two
              different gates that return the same status — check the role chip in the header first.
            </Box>
          )}
          {err.status === 405 && (
            <Box sx={{ mt: 1, fontSize: 13 }}>
              The backend has no POST alias for this report yet. Its 15 report routes are
              GET-with-a-JSON-body, which a browser cannot send.
            </Box>
          )}
        </Alert>
      )}

      {q.isLoading && (
        <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
          <CircularProgress size={20} />
          <Typography variant="body2">Loading…</Typography>
        </Box>
      )}

      {!ready && (
        <Typography variant="body2" color="text.secondary">
          This report needs {required.join(" and ")} before it can run. A range is pre-filled —
          adjust it and choose <strong>Run report</strong>.
        </Typography>
      )}

      {totals && (
        <Typography variant="subtitle2" sx={{ mb: 1 }} data-testid="report-total">
          Total {totals.currency === "INR" || !totals.currency ? "₹" : `${totals.currency} `}
          {totals.total.toLocaleString("en-IN", { maximumFractionDigits: 2 })}
          {typeof totals.count === "number" ? ` from ${totals.count} recharge${totals.count === 1 ? "" : "s"}` : ""}
        </Typography>
      )}

      {shown && <DataTable rows={shown} />}

      {/* An empty answer is only interpretable next to what was asked. Without
          this, "no rows" and "wrong date range" are the same screen. */}
      {rows && rows.length === 0 && (effective.start || effective.end) && (
        <Typography variant="caption" component="div" color="text.secondary" sx={{ mt: 1 }}>
          Queried {effective.start ?? "any"} to {effective.end ?? "any"} for{" "}
          <code>{identity.namespace}</code> and its descendants.
        </Typography>
      )}
    </Box>
  );
}
